import { FC, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Col, Select } from 'antd'
import { nanoid } from 'nanoid'
import shallow from 'zustand/shallow'
import moment, { Moment } from 'moment-timezone'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerButton from '../Button'
import PerFlexRow from '../FlexRow'
import BackgroundBox from '../BackgroundBox'
import PerDatePicker from '../DatePicker'
import PerInputNumber from '../PerInputNumber'
import { EmployeeLevelEnum } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useForecastModalStore, ForecastModalHiringsFilterValues, ForecastModalNewHiring } from '.'
import { useEditForecastPermission } from '../../../modules/pyramids/usePermission'
import { usePyramidsStore } from '../../../modules/pyramids/PyramidsStore'

const { Option } = Select

export const ForecastModalHiringsFilters: FC<unknown> = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [showFilterForm, toggleFilterForm] = useState<boolean>(false)
  const [tableHiringsForm, filterBu, filterEmployeeLevels] = useForecastModalStore(
    (state) => [state.hiringsTableForm, state.filterBu, state.filterEmployeeLevels],
    shallow,
  )
  const [companyId, isAgePyramidVisible] = usePyramidsStore((state) => [state.selected.companyId, state.isAgePyramidVisible], shallow)
  const hasPermission = useEditForecastPermission(companyId)

  const newHiringCount = useRef(0)

  const { control, getValues } = useForm<ForecastModalHiringsFilterValues>({
    defaultValues: {
      howMany: 1,
      dateOfLevelChange: moment().add(1, 'days').toISOString(),
    },
  })

  const filterBuData = filterBu.map((bu) => (
    <Option key={bu.id} value={bu.id} className={bu.isExpired ? 'bu-expired' : undefined}>
      <span className={bu.isExpired ? 'bu-expired' : undefined}>{bu.name}</span>
    </Option>
  ))

  const emmployeeLevelData = filterEmployeeLevels
    .filter((level: EmployeeLevelEnum) => level !== EmployeeLevelEnum.Leaver)
    .map((level: EmployeeLevelEnum) => (
      <Option key={level} value={level}>
        {level}
      </Option>
    ))

  const handleApply = () => {
    const filterValues = getValues()
    const newValues: ForecastModalNewHiring[] = Array.from({ length: filterValues.howMany }, () => ({
      id: `new-hiring-${newHiringCount.current}`,
      key: nanoid(),
      isNewHiring: true,
      newHiringFullName: `New Hiring ${++newHiringCount.current}`,
      businessUnitId: filterValues.bu || undefined,
      salary: undefined,
      bonus: undefined,
      age: filterValues.age || undefined,
      levelChange: filterValues.level,
      dateOfLevelChange: filterValues.dateOfLevelChange,
    }))
    tableHiringsForm.reset({ hirings: [...tableHiringsForm.getValues().hirings, ...newValues] })
  }

  return (
    <>
      <PerFlexRow mt="m" mb="xxxs">
        <PerButton
          text={t('pyramidsView.forecastModal.buttons.addNewHirings')}
          color="primary"
          before={!!showFilterForm ? 'remove_stroke' : 'add_stroke'}
          type="button"
          onClick={() => toggleFilterForm(!showFilterForm)}
          disabled={!hasPermission}
        />
      </PerFlexRow>
      {!!showFilterForm && (
        <PerFlexRow mt="s" className="fullWidth">
          <BackgroundBox className="fullWidth" color="gray-2" noShadow>
            <PerFlexRow mb="l" align="middle" gutter={16}>
              <Col span={4}>
                <PerFlexRow mb="xxxs">
                  <PerText appearance="label-small" fontWeight="normal">
                    {t('pyramidsView.forecastModal.filters.howMany')}
                  </PerText>
                </PerFlexRow>
                <PerFlexRow className="fullWidth">
                  <Controller
                    name="howMany"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <PerInputNumber
                        value={value}
                        onChange={onChange}
                        onClick={(e) => {
                          e.currentTarget.setSelectionRange(0, 1)
                        }}
                        onKeyUp={(e) => e.currentTarget.setSelectionRange(0, 1)}
                        min={1}
                        max={5}
                        maxLength={1}
                        width={'100%'}
                        className="fullWidth"
                        parser={(value) => {
                          if (!!value && !Number(value)) {
                            return 1
                          } else if (!!value && Number(value) > 5) {
                            return 5
                          }
                          return value || 1
                        }}
                      />
                    )}
                  />
                </PerFlexRow>
              </Col>
              <Col span={8}>
                <PerFlexRow mb="xxxs">
                  <PerText appearance="label-small" fontWeight="normal">
                    {t('pyramidsView.forecastModal.filters.currentEmployeeLevel')}
                  </PerText>
                </PerFlexRow>
                <PerFlexRow className="fullWidth">
                  <Controller
                    name="level"
                    control={control}
                    defaultValue={filterEmployeeLevels[0]}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        onChange={onChange}
                        value={value || filterEmployeeLevels[0]}
                        placeholder={t('pyramidsView.forecastModal.common.selectLevel')}
                        style={{ width: '100%' }}
                      >
                        {emmployeeLevelData}
                      </Select>
                    )}
                  />
                </PerFlexRow>
              </Col>
              <Col span={8}>
                {isAgePyramidVisible && (
                  <>
                    <PerFlexRow mb="xxxs">
                      <PerText appearance="label-small" fontWeight="normal">
                        {t('pyramidsView.forecastModal.filters.age')}
                      </PerText>
                    </PerFlexRow>
                    <PerFlexRow className="fullWidth">
                      <Controller
                        name="age"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <PerInputNumber
                            value={value}
                            onChange={onChange}
                            onClick={(e) => {
                              e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)
                            }}
                            min={1}
                            max={99}
                            maxLength={2}
                            width={'100%'}
                            className="fullWidth"
                            parser={(value) => {
                              if (!!value && !Number(value)) {
                                return 1
                              } else if (!!value && Number(value) > 99) {
                                return 99
                              }
                              return value || ''
                            }}
                          />
                        )}
                      />
                    </PerFlexRow>
                  </>
                )}
              </Col>
            </PerFlexRow>
            <PerFlexRow mb="l" align="middle" gutter={16}>
              <Col span={4}>
                <PerFlexRow mb="xxxs">
                  <PerText appearance="label-small" fontWeight="normal">
                    {t('pyramidsView.forecastModal.filters.dateofLevelChange')}
                  </PerText>
                </PerFlexRow>
                <PerFlexRow className="fullWidth">
                  <Controller
                    name="dateOfLevelChange"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <PerDatePicker
                          showToday={false}
                          inputDate={value}
                          dateFormat="DMY"
                          timeZoneAndLocale={user}
                          size="default"
                          width={'100%'}
                          disabledDate={(current: Moment | null) => !!current && current < moment()}
                          onDateChanged={(date) => onChange(date)}
                        />
                      )
                    }}
                  />
                </PerFlexRow>
              </Col>
              <Col span={8}>
                <PerFlexRow mb="xxxs">
                  <PerText appearance="label-small" fontWeight="normal" transform="uppercase">
                    {t('pyramidsView.forecastModal.filters.businessUnitName')}
                  </PerText>
                </PerFlexRow>
                <PerFlexRow className="fullWidth">
                  <Controller
                    name="bu"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        onChange={onChange}
                        value={value || undefined}
                        placeholder={t('pyramidsView.forecastModal.filters.buPlaceholder')}
                        style={{ width: '100%' }}
                      >
                        {filterBuData}
                      </Select>
                    )}
                  />
                </PerFlexRow>
              </Col>
              <Col span={8}></Col>
            </PerFlexRow>
            <PerFlexRow align="middle" justify="end" gutter={16}>
              <PerButton
                text={t('global.button.cancel')}
                color="secondary"
                before="close_stroke"
                type="button"
                onClick={() => toggleFilterForm(false)}
              />
              <PerSpacer ml="xxxs" mr="xxxs" />
              <PerButton text={t('global.button.apply')} color="success" before="check_stroke" type="button" onClick={handleApply} />
            </PerFlexRow>
          </BackgroundBox>
        </PerFlexRow>
      )}
    </>
  )
}
